<template>
  <div class="flex w-full items-center justify-center">
    <div class="vx-col w-full sm:w-full md:w-3/4 lg:w-2/3 xl:w-1/2">
      <vx-card>
        <span class="flex item-center">
          <feather-icon
            icon="ArrowLeftCircleIcon"
            svgClasses="w-7 h-7 hover:text-primary stroke-current"
            @click.stop="cancelPage"
            style="cursor: pointer"
          />
          <span class="h4 ml-2">{{ $t("hives.CreateNewHive") }}</span>
        </span>
        <page-loader :size="'w-10 h-10'" :height="300" class="mt-8" v-if="isPageLoading == true" />
        <form class="mt-6" v-else>
          <div class="vx-row mb-4">
            <div class="vx-col w-full">
              <label class="vs-input--label" for="hiveName"
                >{{ $t("hives.new.Name") }}:</label
              >
              <vs-input
                class="w-full"
                :placeholder="$t('hives.new.Name')"
                v-model="hiveName"
                v-validate="'required'"
                name="item-name"
                color="warning"
              />
              <span
                class="text-danger text-sm"
                v-show="errors.has('item-name')"
                >{{ $t("apiaries.new.Required") }}</span
              >
            </div>
          </div>
          <div class="vx-row mb-4">
            <div class="vx-col w-full">
              <label class="vs-input--label" for="hive-type"
                >{{ $t("hives.new.HiveType") }}:</label
              >
              <v-select
                name="hive-type"
                :options="hiveTypeOptions"
                :placeholder="$t('hives.new.SelectHiveType')"
                v-model="hiveType"
                :clearable="true"
                :searchable="true"
              >
                <template #no-options="{ searching }">
                  <template v-if="searching">
                    {{ $t("form.NoResult") }}
                  </template>
                  <template v-else>
                    {{ $t("form.Empty") }}
                  </template>
                </template>
              </v-select>
            </div>
          </div>
          <div class="vx-row mb-4">
            <div class="vx-col w-full">
              <label class="vs-input--label" for="bee-type"
                >{{ $t("hives.new.BeeType") }}:</label
              >
              <v-select
                name="bee-type"
                :options="beeTypeOptions"
                :placeholder="$t('hives.new.SelectBeeType')"
                v-model="beeType"
                :clearable="true"
                :searchable="true"
              >
                <template v-slot:option="option">
                  <span>
                    {{ option.label }}
                    <small class="ml-1 text-muted"
                      ><em>( {{ option.scientific_name }} )</em></small
                    >
                  </span>
                </template>
                <template #no-options="{ searching }">
                  <template v-if="searching">
                    {{ $t("form.NoResult") }}
                  </template>
                  <template v-else>
                    {{ $t("form.Empty") }}
                  </template>
                </template>
              </v-select>
            </div>
          </div>
          <div class="vx-row mb-4">
            <div class="vx-col w-full">
              <label class="vs-input--label" for="queen-bee-age"
                >{{ $t("hives.new.QueenBeeDateOfBirth") }}:</label
              >
              <date-picker 
                v-model="queenBeeAge" 
                format="MM/YYYY" 
                type="month" 
                :placeholder="$t('hives.new.QueenBeeDateOfBirth')"         
                :disabled-date="disabledDate">
              </date-picker>
            </div>
          </div>
          <div class="vx-row mb-4">
            <div class="vx-col w-full">
              <label for="apiariesValue" class="vs-input--label"
                >{{ $t("hives.new.Apiary") }}:</label
              >
              <v-select
                v-validate="'required'"
                :options="apiaryOptions"
                :clearable="true"
                :placeholder="$t('hives.new.SelectApiary')"
                v-model="apiariesValue"
                @search="handleApiarySearch"
                name="item-apiary"
              >
              <template v-slot:no-options="{ searching }">
                <template v-if="searching">
                  {{ $t("select.NoMatching") }}
                </template>
                <template v-else>
                  {{ $t("select.NoData") }}
                </template>
              </template>
                <template #list-footer>
                  <li
                    ref="load"
                    class="loader"
                    v-if="isApiaryLazyLoadLoading == true"
                  >
                    <div class="flex items-center justify-center w-full">
                      <div>
                        <feather-icon
                          icon="LoaderIcon"
                          class="mr-3 bx-spin text-warning"
                          svgClasses="w-5 h-5"
                        ></feather-icon>
                      </div>
                    </div>
                    <infinite-loading @infinite="apiaryInfiniteHandler">
                      <div slot="spinner"></div>
                      <div slot="no-more"></div>
                      <div slot="no-results"></div>
                    </infinite-loading>
                  </li>
                </template>
              </v-select>

              <span
                class="text-danger text-sm"
                v-show="errors.has('item-apiary')"
                >{{ $t("hives.new.Required") }}</span
              >
            </div>
          </div>
          <div class="vx-row mb-4">
            <div class="vx-col w-full">
              <label class="vs-input--label"
                >{{ $t("hives.Device.Title") }}:</label
              >
              <v-select
                :options="deviceOptions"
                :clearable="true"
                :searchable="false"
                :placeholder="$t('hives.Device.SelectDevice')"
                v-model="deviceValue"
                name="item-user-company"
              >
              <template v-slot:no-options="{ searching }">
                <template v-if="searching">
                  {{ $t("select.NoMatching") }}
                </template>
                <template v-else>
                  {{ $t("select.NoData") }}
                </template>
              </template>
                <template #list-footer>
                  <li
                    ref="load"
                    class="loader"
                    v-if="isDeviceLazyLoadLoading == true"
                  >
                    <div class="flex items-center justify-center w-full">
                      <div>
                        <feather-icon
                          icon="LoaderIcon"
                          class="mr-3 bx-spin text-warning"
                          svgClasses="w-5 h-5"
                        ></feather-icon>
                      </div>
                    </div>
                    <infinite-loading @infinite="deviceInfiniteHandler">
                      <div slot="spinner"></div>
                      <div slot="no-more"></div>
                      <div slot="no-results"></div>
                    </infinite-loading>
                  </li>
                </template>
              </v-select>
              <template v-if="deviceOptions.length == 0">
                <vs-alert class="mt-3 h-auto" color="warning">
                  <div class="text-center">
                    {{ $t("hives.Device.EmptyWarningMessage") }}
                  </div>
                  <div class="text-center mt-3">
                    <div class="flex justify-center">
                      <vs-button
                        class="text-white ml-3 px-4 py-2 text-sm"
                        color="warning"
                        type="filled"
                        icon-pack="feather"
                        icon="icon-plus"
                        @click="addDevice"
                        >{{ $t("hives.detail.AddDevice") }}</vs-button
                      >
                    </div>
                  </div>
                </vs-alert>
              </template>
            </div>
          </div>
          <vs-divider class="customer-divider my-6" />

          <div class="vx-row">
            <div class="vx-col w-full text-right">
              <vs-button
                color="warning"
                type="filled"
                @click.prevent="submitForm"
                >{{ $t("hives.new.Save") }}
                <feather-icon
                  class="ml-3 bx-spin"
                  :class="isSubmitFormLoading == true ? '' : 'd-none'"
                  icon="LoaderIcon"
                  svgClasses="w-5 h-5"
                ></feather-icon
              ></vs-button>
            </div>
          </div>
        </form>
      </vx-card>
    </div>
  </div>
</template>
<script>
import i18n from '../../../i18n/i18n.js'
const language = i18n.locale

import vSelect from 'vue-select'
const moment = require('moment')

import DatePicker from 'vue2-datepicker'
require('vue2-datepicker/index.css')
require(`vue2-datepicker/locale/${language}`)

import InfiniteLoading from 'vue-infinite-loading'
import PageLoader from '../../../components/Loader.vue'
export default {
  components: {
    vSelect,
    DatePicker,
    InfiniteLoading,
    PageLoader
  },
  data () {
    return {
      hiveName: '',
      beeType: '',
      beeTypeOptions: [],
      hiveType:'',
      hiveTypeOptions: [],
      apiariesValue: null,
      apiaryOptions: [],
      deviceOptions: [],
      deviceValue: null,
      apiaryCursor: null,
      isApiaryLazyLoadLoading: true,
      isSubmitFormLoading: false,
      queenBeeAge:null,
      isDeviceLazyLoadLoading: true,
      deviceCursor: null,
      isPageLoading: true
    }
  },
  computed: {
    userInfo () {
      return this.$store.getters.returnUserInfo
    },
    userRole () {
      return this.$store.getters.userRole
    },
    newDevice () {
      return this.$store.getters.newDevice
    },
    newHiveData () {
      return this.$store.getters.newHiveData
    }
  },
  mounted () {
    if (this.userRole !== 'user') {
      this.getApiaries(`?user=${  this.userInfo.user_id}`)
    } else {
      this.getApiaries('')
    }
    this.getDeviceList()
    this.getBeeType()
    this.getHiveType()

    if (this.newDevice) {
      this.deviceValue = {                  
        value: this.newDevice.id,
        label: this.newDevice.device_serial
      }
      this.$store.dispatch('addNewDeviceData', null)
    }

    if (this.newHiveData) {
      this.hiveName = this.newHiveData.name
      this.apiariesValue = this.newHiveData.apiary
      this.bee_type = this.newHiveData.bee_type
      this.hiveType = this.newHiveData.hive_type
      this.queenBeeAge = this.newHiveData.queen_birthdate
      this.$store.dispatch('getNewHiveData', null)
    }
    window.addEventListener('beforeunload', this.beforeWindowUnload)
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.beforeWindowUnload)
  },
  methods: {
    beforeWindowUnload (e) {
      e.preventDefault()
      e.returnValue = ''
    },
    disabledDate (date) {
      const month = new Date(moment().endOf('month')).getTime()
      return new Date(date).getTime() > month
    },
    handleApiarySearch (search) {
      let searchData = `?search=${search}`
      if (this.userRole !== 'user') {
        searchData = `?user=${  this.userInfo.user_id}&search=${search}`
      }
      this.getApiaries(searchData)
    },
    getApiaries (params) {
      this.$store
        .dispatch('getApiariesData', params)
        .then((response) => {
          this.apiaryOptions = []

          if (response.data.results.length) {
            response.data.results.map((value) => {
              this.apiaryOptions.push({
                label: value.name,
                value: value.id
              })
            })
          }

          this.apiaryCursor = response.data.next
          if (response.data.next === null) {
            this.isApiaryLazyLoadLoading = false
          } else {
            this.isApiaryLazyLoadLoading = true
          }
          this.isPageLoading = false
        })
        .catch((error) => {
          this.isPageLoading = false
          this.errorMsg(error)
        })
    },
    apiaryInfiniteHandler ($state) {
      if (this.apiaryCursor !== null) {
        this.$store
          .dispatch('getApiariesLazyLoadData', this.apiaryCursor)
          .then((response) => {
            const lazyLoadData = []
            if (response.data.results.length) {
              response.data.results.map((value, index) => {
                lazyLoadData.push({
                  label: value.name,
                  value: value.id
                })
                if (index + 1 === response.data.results.length) {
                  this.apiaryOptions.push(...lazyLoadData)
                }
              })
            }
            this.apiaryCursor = response.data.next
            if (response.data.next === null) {
              this.isApiaryLazyLoadLoading = false
              $state.complete()
            } else {
              this.isApiaryLazyLoadLoading = true
              $state.loaded()
            }
          })
          .catch((error) => {
            this.errorMsg(error)
          })
      }
    },
    deviceInfiniteHandler ($state) {
      if (this.deviceCursor !== null) {
        this.$store
          .dispatch('getHiveDeviceLazyLoadData', this.deviceCursor)
          .then((response) => {
            const lazyLoadData = []
            if (response.data.results.length) {
              response.data.results.map((value, index) => {
                lazyLoadData.push({
                  value: value.id,
                  label: value.device_serial
                })
                if (index + 1 === response.data.results.length) {
                  this.deviceOptions.push(...lazyLoadData)
                }
              })
            }
            this.deviceCursor = response.data.next
            if (response.data.next === null) {
              this.isDeviceLazyLoadLoading = false
              $state.complete()
            } else {
              this.isDeviceLazyLoadLoading = true
              $state.loaded()
            }
          })
          .catch((error) => {
            this.errorMsg(error)
          })
      }
    },
    getDeviceList () {
      this.$store
        .dispatch('getHiveDeviceData', '')
        .then((response) => {
          this.deviceOptions = []
          if (response.data.results.length) {
            response.data.results.map((value) => {
              this.deviceOptions.push({
                value: value.id,
                label: value.device_serial
              })
            })
          }

          this.deviceCursor = response.data.next

          if (response.data.next === null) {
            this.isDeviceLazyLoadLoading = false
          } else {
            this.isDeviceLazyLoadLoading = true
          }
        })
        .catch((error) => {
          this.errorMsg(error)
        })
    },
    getBeeType () {
      this.$store
        .dispatch('getBeeType')
        .then((response) => {
          this.beeTypeOptions = []
          response.data.map((val) => {
            this.beeTypeOptions.push({
              value: val.id,
              label: val.name,
              scientific_name: val.scientific_name
            })
          })
        })
        .catch((error) => {
          this.errorMsg(error)
        })
    },
    getHiveType () {
      this.$store
        .dispatch('getHiveType')
        .then((response) => {
          this.hiveTypeOptions = []
          response.data.map((val) => {
            this.hiveTypeOptions.push({
              value: val.id,
              label: val.name
            })
          })
        })
        .catch((error) => {
          this.errorMsg(error)
        })
    },
    cancelPage () {
      this.$router.back()
    },
    addDevice () {
      this.$store.dispatch('getNewHiveData', {
        name: this.hiveName,
        apiary: this.apiariesValue,
        bee_type:this.beeType && this.beeType.value ? this.beeType : null,
        hive_type:this.hiveType && this.hiveType.value ? this.hiveType : null,
        queen_birthdate:this.queenBeeAge ? this.queenBeeAge : null
      })
      this.$router.push('/devices/new').catch(() => {})
    },
    submitForm () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.isSubmitFormLoading = true
          const data = {
            name: this.hiveName,
            apiary: this.apiariesValue.value,
            device: this.deviceValue ? this.deviceValue.value : null,
            bee_type:this.beeType && this.beeType.value ? this.beeType.value : null,
            hive_type:this.hiveType && this.hiveType.value ? this.hiveType.value : null,
            queen_birthdate:this.queenBeeAge ? moment(this.queenBeeAge).format('YYYY-MM-DD') : null
          }

          if (this.deviceValue) {
            data['device'] = this.deviceValue.value
          }
          this.$store
            .dispatch('createHive', data)
            .then(() => {
              this.isSubmitFormLoading = false
              this.$vs.notify({
                title: i18n.t('notifyAlert.success.title'),
                text: i18n.t('hives.new.SuccessMessage'),
                iconPack: 'feather',
                icon: 'icon-check',
                color: 'success',
                position: 'top-right'
              })
              this.$store.dispatch('getNewHiveData', null)
              this.$router.back()
            })
            .catch((error) => {
              this.isSubmitFormLoading = false
              this.errorMsg(error)
            })
        }
      })
    },
    errorMsg (error) {
      this.$store.dispatch('errorMsg', error).then((res) => {
        this.$vs.notify({
          title: i18n.t('notifyAlert.error.title'),
          text: res,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: 'top-right'
        })
      })
    }
  }
}
</script>
<style lang="scss" scoped>
.vs-input--label {
  font-size: 0.9rem !important;
  font-weight: 500 !important;
}
.height-300 {
  height: 300px;
}

.not-apiaries-part {
  img {
    margin: 0px auto;
    display: flex;
  }
  .vs-alert span {
    font-size: 15px !important;
  }
}
@media (max-width: 500px) {
  .not-apiaries-part {
    img {
      width: 200px;
    }
    .apiaries-add-text {
      font-size: 20px;
    }
    .vs-alert span {
      font-size: 13px !important;
    }
  }
}
</style>